/**
 * Why a quote is revised (Lucid 007, docs/lucid-decisions/007-jobs-revisions-and-splits.md).
 *
 * Peter, 2026-09-25: every revision records WHY it was made. The reasons he
 * named are an expired quote, legitimate changes the client asked for, defects
 * that have worsened since the visit, and further issues found on site. "Other"
 * covers the rest; the note always says it in words.
 *
 * This module is the one list of reason kinds and the one check of a reason on
 * the client. The New revision sheet, the Split sheet (a split of an issued
 * quote makes a new revision of what stays) and the QM Assistant use it. The
 * server keeps the same list in backend/src/services/quote-job-events.js
 * (REVISION_REASON_KINDS); a parity test reads that file as text, because the
 * backend never imports src/ (CLAUDE.md backend Rule 4).
 *
 * The note keeps its line breaks. The only clean-up is the server's: CRLF
 * becomes LF and the ends are trimmed, and the 10 to 4,000 character rule
 * counts that text, so the browser and the server agree on every note.
 */

export const REVISION_REASON_KINDS = [
    'quote_expired',
    'client_changes',
    'condition_worsened',
    'found_on_site',
    'other',
] as const;

export type RevisionReasonKind = typeof REVISION_REASON_KINDS[number];

/** A reason note must say something: at least this many characters once trimmed. */
export const REVISION_REASON_NOTE_MIN = 10;

/** The longest note kept, the same bound as quote_job_events.note (migration 122). */
export const REVISION_REASON_NOTE_MAX = 4000;

export interface RevisionReason {
    kind: RevisionReasonKind;
    note: string;
    /** A new site visit is needed for this revision. Absent means no. */
    siteVisitNeeded?: boolean;
}

/** What a reason form holds while the operator fills it in. */
export interface RevisionReasonDraft {
    kind: RevisionReasonKind | null;
    note: string;
    siteVisitNeeded: boolean;
}

/** Each kind in the operator's words (JOBS-UX-SPEC 3.1). */
export const REVISION_REASON_LABELS: Record<RevisionReasonKind, string> = {
    quote_expired: 'Quote expired',
    client_changes: 'Client asked for changes',
    condition_worsened: 'Condition has worsened',
    found_on_site: 'Further issues found on site',
    other: 'Other',
};

/**
 * Whether the "A new site visit is needed" box shows for a kind, and its
 * starting tick. null hides the box, and the reason then never asks for a visit.
 */
export const REVISION_REASON_SITE_VISIT_DEFAULT: Record<RevisionReasonKind, boolean | null> = {
    quote_expired: null,
    client_changes: null,
    condition_worsened: true,
    found_on_site: false,
    other: null,
};

/**
 * The class the reason's reference file carries. A reason that changes what
 * is quoted is part of the request ('rfq'); an expired quote or another
 * reason is not ('other'), so it does not mark the RFQ review stale.
 */
export function revisionReasonClass(kind: RevisionReasonKind): 'rfq' | 'other' {
    return kind === 'quote_expired' || kind === 'other' ? 'other' : 'rfq';
}

/**
 * What the QM Assistant is told when it proposes a revision without a usable
 * reason. The reason is the operator's, so the model must ask, never guess.
 */
export const ASK_OPERATOR_FOR_REVISION_REASON = 'Ask the operator why this quote is being revised (the quote expired, the client asked for changes, the condition has worsened, further issues were found on site, or another reason) and what changed, in their words, then propose it again with that reason. Do not guess the reason.';

export function isRevisionReasonKind(value: unknown): value is RevisionReasonKind {
    return typeof value === 'string' && (REVISION_REASON_KINDS as readonly string[]).includes(value);
}

/** The note as it is sent and counted: CRLF becomes LF and the ends are trimmed. Nothing else. */
export function normaliseRevisionNote(note: unknown): string {
    return typeof note === 'string' ? note.replace(/\r\n/g, '\n').trim() : '';
}

export type RevisionReasonCheck =
    | { ok: true; reason: Required<RevisionReason> }
    | { ok: false; problem: string };

/**
 * Check a reason and return it cleaned. `siteVisitNeeded` absent takes the
 * kind's default; a kind whose box is hidden never asks for a visit. A
 * problem is worded for whoever has to fix it, which is usually the operator
 * or the Assistant asking the operator.
 */
export function checkRevisionReason(kind: unknown, note: unknown, siteVisitNeeded?: unknown): RevisionReasonCheck {
    if (!isRevisionReasonKind(kind)) {
        return {
            ok: false,
            problem: `The reason kind must be one of ${REVISION_REASON_KINDS.join(', ')}.`,
        };
    }
    const text = normaliseRevisionNote(note);
    if (text.length < REVISION_REASON_NOTE_MIN) {
        return {
            ok: false,
            problem: `The reason note must say why in at least ${REVISION_REASON_NOTE_MIN} characters.`,
        };
    }
    if (text.length > REVISION_REASON_NOTE_MAX) {
        return {
            ok: false,
            problem: `Keep the reason note to ${REVISION_REASON_NOTE_MAX.toLocaleString('en-AU')} characters.`,
        };
    }
    if (siteVisitNeeded !== undefined && siteVisitNeeded !== null && typeof siteVisitNeeded !== 'boolean') {
        return { ok: false, problem: 'Say whether a new site visit is needed with true or false.' };
    }
    const shown = REVISION_REASON_SITE_VISIT_DEFAULT[kind];
    const visit = shown === null
        ? false
        : typeof siteVisitNeeded === 'boolean' ? siteVisitNeeded : shown;
    return { ok: true, reason: { kind, note: text, siteVisitNeeded: visit } };
}

/**
 * Why the Create or Split button is still disabled, in words, or null when
 * the draft is ready. `action` finishes the sentence: "to create Rev 2".
 */
export function revisionReasonDraftProblem(draft: RevisionReasonDraft, action: string): string | null {
    const length = normaliseRevisionNote(draft.note).length;
    if (!draft.kind || length < REVISION_REASON_NOTE_MIN) {
        return `Choose a reason and write at least ${REVISION_REASON_NOTE_MIN} characters ${action}.`;
    }
    if (length > REVISION_REASON_NOTE_MAX) {
        return `Keep the note to ${REVISION_REASON_NOTE_MAX.toLocaleString('en-AU')} characters ${action}.`;
    }
    return null;
}

/** The draft a form starts from. An expired quote starts on Quote expired with an editable line. */
export function initialRevisionReasonDraft(input: {
    quoteStatus?: string | null;
    issuedBy?: 'sent' | 'pdf' | null;
    issuedAtWords?: string | null;
} = {}): RevisionReasonDraft {
    if (String(input.quoteStatus || '').toLowerCase() !== 'expired') {
        return { kind: null, note: '', siteVisitNeeded: false };
    }
    const verb = input.issuedBy === 'pdf' ? 'Issued' : 'Sent';
    const opening = input.issuedAtWords ? `${verb} ${input.issuedAtWords}, now expired.` : 'Now expired.';
    return { kind: 'quote_expired', note: `${opening} Re-quoted at current rates.`, siteVisitNeeded: false };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
let sydneyParts: Intl.DateTimeFormat | null = null;

/**
 * "12 Sep 2026", or "12 Sep" with `year: false`, on the Sydney calendar.
 * A fixed month table: Intl's short month prints "Sept" (JOBS-UX-SPEC 1.5).
 * '' when the value is not a date.
 */
export function formatRevisionReasonDate(value: string | null | undefined, { year = true }: { year?: boolean } = {}): string {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    sydneyParts ??= new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'numeric', year: 'numeric', timeZone: 'Australia/Sydney' });
    const parts: Record<string, string> = {};
    sydneyParts.formatToParts(date).forEach((part) => { parts[part.type] = part.value; });
    const month = MONTHS[Number(parts.month) - 1];
    const day = Number(parts.day);
    if (!month || !Number.isFinite(day)) return '';
    return year && parts.year ? `${day} ${month} ${parts.year}` : `${day} ${month}`;
}

/** "Client asked for changes: Owners want the balcony tiles changed to 600 mm." */
export function describeRevisionReason(reason: RevisionReason | null | undefined): string {
    if (!reason || !isRevisionReasonKind(reason.kind)) return '';
    const note = String(reason.note || '').trim();
    const label = REVISION_REASON_LABELS[reason.kind];
    return note ? `${label}: ${note}` : label;
}
